import * as THREE from 'three';

export const TAU = Math.PI * 2;
export const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
export const lerp = (a, b, t) => a + (b - a) * t;
export const invLerp = (a, b, x) => (x - a) / (b - a);
export const smoothstep = (a, b, x) => {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};
/** Frame-rate independent exponential smoothing. */
export const damp = (a, b, lambda, dt) => lerp(a, b, 1 - Math.exp(-lambda * dt));
export const wrapAngle = (a) => {
  a = (a + Math.PI) % TAU;
  if (a < 0) a += TAU;
  return a - Math.PI;
};
export const angleDiff = (from, to) => wrapAngle(to - from);
export const dampAngle = (a, b, lambda, dt) => a + angleDiff(a, b) * (1 - Math.exp(-lambda * dt));

// ---------------------------------------------------------------- seeded random
let _state = 1337;
export function seedRandom(seed) {
  _state = (seed >>> 0) || 1;
  initNoise(seed);
}
/** World seed: ?seed=123 in the URL makes a different (but stable) village. */
export const SEED = (typeof location !== 'undefined' && Number(new URLSearchParams(location.search).get('seed'))) || 20240915;
export function rnd() {
  _state = (_state + 0x6d2b79f5) | 0;
  let t = Math.imul(_state ^ (_state >>> 15), 1 | _state);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
export const rr = (a = 0, b = 1) => a + (b - a) * rnd();
export const chance = (p) => rnd() < p;
export const pick = (arr) => arr[Math.floor(rnd() * arr.length)];
export const rsign = () => (rnd() < 0.5 ? -1 : 1);

/** Stateless integer hash -> [0,1). */
export function hash2(x, y, s = 0) {
  let h = (x * 374761393 + y * 668265263 + s * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// ---------------------------------------------------------------- perlin noise
const perm = new Uint8Array(512);
function initNoise(seed) {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  let s = (seed >>> 0) || 1;
  for (let i = 255; i > 0; i--) {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    const j = s % (i + 1);
    const t = p[i];
    p[i] = p[j];
    p[j] = t;
  }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
}
initNoise(1337);
seedRandom(SEED);

const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
function grad(h, x, y) {
  switch (h & 7) {
    case 0: return x + y;
    case 1: return -x + y;
    case 2: return x - y;
    case 3: return -x - y;
    case 4: return x;
    case 5: return -x;
    case 6: return y;
    default: return -y;
  }
}
/** 2D perlin noise, roughly in [-1, 1]. */
export function noise2(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const X = xi & 255, Y = yi & 255;
  const u = fade(xf), v = fade(yf);
  const aa = perm[perm[X] + Y], ab = perm[perm[X] + Y + 1];
  const ba = perm[perm[X + 1] + Y], bb = perm[perm[X + 1] + Y + 1];
  const x1 = lerp(grad(aa, xf, yf), grad(ba, xf - 1, yf), u);
  const x2 = lerp(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u);
  return lerp(x1, x2, v) * 1.41;
}
export function fbm2(x, y, oct = 4, lac = 2, gain = 0.5) {
  let a = 1, f = 1, sum = 0, norm = 0;
  for (let i = 0; i < oct; i++) {
    sum += a * noise2(x * f, y * f);
    norm += a;
    a *= gain;
    f *= lac;
  }
  return sum / norm;
}

// ---------------------------------------------------------------- colours
const _c = new THREE.Color();
/** Hex/CSS -> linear-space THREE.Color (cached-free; cheap enough at build time). */
export const col = (v) => new THREE.Color(v);
export function mixColor(out, a, b, t) {
  out.copy(a).lerp(b, t);
  return out;
}
export function tint(color, amount) {
  _c.set(color);
  _c.offsetHSL(0, 0, amount);
  return _c.clone();
}

// ---------------------------------------------------------------- misc
export function nearestPow2(n) {
  return 2 ** Math.round(Math.log2(n));
}
export const isTouch = () => matchMedia('(pointer: coarse)').matches;
